<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';

defineProps({
  threads: {
    type: Array,
    default: () => [],
  },
  activeThreadId: {
    type: Number,
    default: null,
  },
  activeThreadTitle: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['selectThread', 'reset']);
const { t } = useI18n();
const isOpen = ref(false);

const formatTimestamp = createdAt => {
  return createdAt ? shortTimestamp(dynamicTime(createdAt)) : '';
};

const toggleList = () => {
  isOpen.value = !isOpen.value;
};

const handleReset = () => {
  emit('reset');
  isOpen.value = false;
};

const handleSelectThread = thread => {
  emit('selectThread', thread);
  isOpen.value = false;
};
</script>

<template>
  <div class="w-full">
    <button
      class="w-full max-w-full rounded-lg border border-n-weak px-3 py-2 text-left transition-colors hover:bg-n-alpha-1"
      :class="{ 'bg-n-alpha-2 border-n-strong': isOpen }"
      @click="toggleList"
    >
      <div class="flex items-center gap-3 min-w-0">
        <div
          class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-n-slate-11"
        >
          <i class="i-lucide-history size-4" />
        </div>
        <div class="min-w-0 flex-1">
          <div class="text-xs font-medium text-n-slate-12">
            {{ t('WIZARD.COPILOT.RECENT_SESSIONS') }}
          </div>
          <div class="truncate text-sm text-n-slate-11">
            {{
              activeThreadTitle || t('WIZARD.COPILOT.NEW_CONVERSATION_LABEL')
            }}
          </div>
        </div>
        <i
          class="i-lucide-chevron-down size-4 shrink-0 text-n-slate-10 transition-transform"
          :class="{ 'rotate-180': isOpen }"
        />
      </div>
    </button>

    <div
      v-if="isOpen"
      class="mt-2 rounded-xl border border-n-weak bg-n-surface-1 p-2 shadow-sm"
    >
      <button
        class="w-full rounded-lg px-3 py-2 text-left transition-colors hover:bg-n-alpha-2"
        @click="handleReset"
      >
        <div class="flex flex-col gap-0.5">
          <span class="text-sm text-n-slate-12">
            {{ t('WIZARD.COPILOT.NEW_CONVERSATION_LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('WIZARD.COPILOT.NEW_CONVERSATION_DESC') }}
          </span>
        </div>
      </button>

      <div class="mt-2 border-t border-n-weak pt-2">
        <div class="px-3 pb-2 text-xs font-medium text-n-slate-10">
          {{ t('WIZARD.COPILOT.RECENT_SESSIONS') }}
        </div>
        <div
          v-if="threads.length"
          class="max-h-80 space-y-2 overflow-y-auto px-1 pb-1"
        >
          <button
            v-for="thread in threads"
            :key="thread.id"
            class="flex w-full items-start justify-between gap-2 rounded-lg border border-transparent px-3 py-2 text-left transition-colors hover:bg-n-alpha-1"
            :class="{
              '!bg-n-alpha-2 !border-n-weak': thread.id === activeThreadId,
            }"
            @click="handleSelectThread(thread)"
          >
            <div class="min-w-0 flex flex-1 flex-col items-start gap-1">
              <span class="line-clamp-1 text-sm text-n-slate-12">
                {{ thread.title }}
              </span>
              <div
                class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-n-slate-11"
              >
                <span class="line-clamp-1">
                  {{
                    thread.assistant?.name ||
                    t('WIZARD.COPILOT.UNKNOWN_ASSISTANT')
                  }}
                </span>
                <i class="i-lucide-dot size-3 text-n-slate-10" />
                <span class="text-n-slate-10">
                  {{ formatTimestamp(thread.created_at) }}
                </span>
                <span
                  v-if="thread.summary_present"
                  class="inline-flex items-center rounded-full bg-n-iris-3/70 px-2 py-0.5 text-[11px] leading-4 text-n-iris-11"
                >
                  {{ t('WIZARD.COPILOT.SUMMARY_BADGE') }}
                </span>
              </div>
            </div>
            <div
              v-if="thread.id === activeThreadId"
              class="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-n-slate-12 dark:bg-n-slate-11"
            >
              <i class="i-lucide-check size-3 text-white dark:text-n-slate-1" />
            </div>
          </button>
        </div>
        <div v-else class="px-3 py-2 text-xs text-n-slate-11">
          {{ t('WIZARD.COPILOT.NO_RECENT_SESSIONS') }}
        </div>
      </div>
    </div>
  </div>
</template>
